import { useContext, useEffect, useState } from 'react';
import { Box, SelectChangeEvent, Tab, Tabs } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { fetchFacilities } from 'api/facility';
import { loadYardGeometry, YardGeometryProps } from 'api/yardGeometry';
import { UserContext } from '../../App';
import { isFreightrollUser } from '../../utils/roleHelper';
import {
  Facility,
  loadFacilityFromStorage,
  saveFacilityToStorage
} from '../../utils/facilityStorage';
import FacilitySelector from '../../components/FacilitySelector';
import { facilityDisplayName } from 'utils/campusLabels';
import YardChecksOverview from './YardChecksOverview';
import YardChecksExceptions from './YardChecksExceptions';

const TABS = ['findings', 'overview'] as const;
type YardChecksTab = (typeof TABS)[number];

const YardChecks = () => {
  // @ts-ignore
  const { user } = useContext(UserContext);
  const showOverviewTab = isFreightrollUser(user);

  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab: YardChecksTab =
    TABS.find((t) => t === requested && (t !== 'overview' || showOverviewTab)) ??
    'findings';

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() =>
    loadFacilityFromStorage()
  );
  const [facilitiesError, setFacilitiesError] = useState<string | null>(null);
  const [geometry, setGeometry] = useState<GeoJSON.FeatureCollection<
    GeoJSON.Geometry,
    YardGeometryProps
  > | null>(null);

  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };

  // Load facilities on mount, defaulting/validating the facility loaded from storage above
  useEffect(() => {
    fetchFacilities()
      .then((result) => {
        setFacilities(result);

        const storedStillValid =
          selectedFacility && result.some((f) => f.id === selectedFacility.id);
        if (!storedStillValid && result.length > 0) {
          setSelectedFacility(result[0]);
        }
      })
      .catch(() => setFacilitiesError('Failed to load facilities'));
    // Intentionally run once on mount: `selectedFacility` here is only ever the value loaded from storage at init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load yard spot geometry whenever the selected facility changes, so it's
  // ready before either tab's detail view opens — shared here since both the
  // Overview and Exceptions detail views place a read on the same map.
  useEffect(() => {
    if (!selectedFacility) {
      setGeometry(null);
      return;
    }
    let cancelled = false;
    loadYardGeometry(selectedFacility.id)
      .then((result) => {
        if (!cancelled) setGeometry(result);
      })
      // eslint-disable-next-line no-console
      .catch((err) => console.error('Failed to load yard spot geometry', err));
    return () => {
      cancelled = true;
    };
  }, [selectedFacility]);

  const handleFacilityChange = (event: SelectChangeEvent<any>) => {
    const facility = facilities.find((f) => f.id === event.target.value);
    setSelectedFacility(facility || null);
  };

  const handleTabChange = (_: unknown, value: YardChecksTab) => {
    setSearchParams(value === 'findings' ? {} : { tab: value }, { replace: true });
  };

  return (
    <div className="p-5">
      <h1 className="text-2xl font-bold mb-2">RTLS Findings</h1>

      <div className="bg-white p-4 shadow mb-4">
        <FacilitySelector
          facilities={facilities.map((f) => ({ id: f.id, name: facilityDisplayName(f) }))}
          value={selectedFacility?.id ?? ''}
          onChange={handleFacilityChange}
          showId
          sx={{ minWidth: 220 }}
        />
      </div>

      {facilitiesError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
          {facilitiesError}
        </div>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label="RTLS Findings" value="findings" />
          {showOverviewTab && <Tab label="Overview" value="overview" />}
        </Tabs>
      </Box>

      {tab === 'overview' && showOverviewTab ? (
        <YardChecksOverview selectedFacility={selectedFacility} geometry={geometry} />
      ) : (
        <YardChecksExceptions selectedFacility={selectedFacility} geometry={geometry} />
      )}
    </div>
  );
};

export default YardChecks;
